// Eskom PARCC — generic data table primitive.
//
// Wraps the monolith's `.table-wrap` + `<table>` (sticky `th`, hover rows) with
// a typed, reusable column model and built-in client-side sort. Presentational
// only: it sorts and renders the rows it is given — it never fetches.
//
// The sort comparator is extracted into the pure, exported `sortRows` so it can
// be unit-tested in isolation. It is a *stable* sort (preserves input order for
// equal keys) and sorts null/undefined consistently to the end (asc) / start
// (desc) so a half-populated column never reorders unpredictably.
import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';

export type SortDir = 'asc' | 'desc';

export interface Column<T> {
  /** Stable identifier for the column (also the default value accessor key). */
  key: string;
  header: ReactNode;
  /** Custom cell renderer; defaults to the raw value at `key`. */
  render?: (row: T) => ReactNode;
  /** When true, the header toggles sort on this column. */
  sortable?: boolean;
  align?: 'left' | 'right' | 'center';
  /**
   * Value used for sorting (and the default render) — defaults to `row[key]`.
   * Supply this when the sort value differs from what is displayed.
   */
  accessor?: (row: T) => unknown;
}

/** Default accessor: read `row[key]` treating the row as a string-keyed record. */
function defaultAccessor<T>(key: string): (row: T) => unknown {
  return (row: T) => (row as Record<string, unknown>)[key];
}

/**
 * Stable sort of `rows` by the value returned from `accessor`, ascending or
 * descending. Strings compare with `localeCompare`; numbers numerically; mixed
 * or other types fall back to string comparison. Nullish values always sort
 * after present values in ascending order (and before, in descending), so they
 * cluster at the bottom of a sorted-down column rather than scattering.
 */
export function sortRows<T>(
  rows: readonly T[],
  _key: string,
  dir: SortDir,
  accessor: (row: T) => unknown,
): T[] {
  const factor = dir === 'asc' ? 1 : -1;
  // Decorate with original index to keep the sort stable across engines.
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const cmp = compareValues(accessor(a.row), accessor(b.row));
      if (cmp !== 0) return cmp * factor;
      return a.index - b.index; // stable tie-break (not affected by direction)
    })
    .map((entry) => entry.row);
}

function isNullish(v: unknown): v is null | undefined {
  return v === null || v === undefined;
}

/** Compare two cell values; nullish always sorts last in ascending order. */
function compareValues(a: unknown, b: unknown): number {
  const aNull = isNullish(a) || (typeof a === 'number' && Number.isNaN(a));
  const bNull = isNullish(b) || (typeof b === 'number' && Number.isNaN(b));
  if (aNull && bNull) return 0;
  if (aNull) return 1; // a after b
  if (bNull) return -1; // b after a

  if (typeof a === 'number' && typeof b === 'number') {
    return a < b ? -1 : a > b ? 1 : 0;
  }
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

export function DataTable<T>({
  columns,
  rows,
  getRowKey,
  onRowClick,
  emptyMessage = 'No records to display.',
  initialSort,
  selectedRowKey,
}: {
  columns: Column<T>[];
  rows: T[];
  /** Stable React key for a row; defaults to its array index. */
  getRowKey?: (row: T, index: number) => string | number;
  onRowClick?: (row: T) => void;
  emptyMessage?: ReactNode;
  /**
   * Seeds the internal sort state so the table renders sorted on first mount.
   * The user can still click a header to change it afterwards.
   */
  initialSort?: { key: string; dir: SortDir };
  /**
   * When a row's key (via `getRowKey`) equals this, that `<tr>` gets the
   * `row-selected` class for a visual highlight. Selection is owned by the
   * caller; this prop is purely presentational and never affects `onRowClick`.
   */
  selectedRowKey?: string | number;
}): React.JSX.Element {
  // Seed from `initialSort` so the first render is already sorted; the user can
  // still toggle it via the header buttons (initialiser runs once, on mount).
  const [sort, setSort] = useState<{ key: string; dir: SortDir } | null>(
    () => initialSort ?? null,
  );

  const accessorFor = useMemo(() => {
    const map = new Map<string, (row: T) => unknown>();
    for (const col of columns) {
      map.set(col.key, col.accessor ?? defaultAccessor<T>(col.key));
    }
    return map;
  }, [columns]);

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const accessor = accessorFor.get(sort.key);
    if (!accessor) return rows;
    return sortRows(rows, sort.key, sort.dir, accessor);
  }, [rows, sort, accessorFor]);

  function toggleSort(key: string): void {
    setSort((current) => {
      if (!current || current.key !== key) return { key, dir: 'asc' };
      return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
    });
  }

  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            {columns.map((col) => {
              const active = sort?.key === col.key;
              const arrow = active ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '';
              const style = col.align ? { textAlign: col.align } : undefined;
              if (!col.sortable) {
                return (
                  <th key={col.key} scope="col" style={style}>
                    {col.header}
                  </th>
                );
              }
              return (
                <th
                  key={col.key}
                  scope="col"
                  style={style}
                  aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                >
                  <button
                    type="button"
                    onClick={() => toggleSort(col.key)}
                    style={{
                      background: 'none',
                      border: 0,
                      padding: 0,
                      font: 'inherit',
                      color: 'inherit',
                      cursor: 'pointer',
                      userSelect: 'none',
                      display: 'inline-flex',
                      gap: '6px',
                      alignItems: 'center',
                    }}
                  >
                    {col.header}
                    {arrow}
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedRows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="empty">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            sortedRows.map((row, index) => {
              const rowKey = getRowKey ? getRowKey(row, index) : index;
              const isSelected = selectedRowKey !== undefined && rowKey === selectedRowKey;
              return (
              <tr
                key={rowKey}
                className={isSelected ? 'row-selected' : undefined}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                style={onRowClick ? { cursor: 'pointer' } : undefined}
              >
                {columns.map((col) => {
                  const style = col.align ? { textAlign: col.align } : undefined;
                  const content = col.render
                    ? col.render(row)
                    : asNode((accessorFor.get(col.key) ?? defaultAccessor<T>(col.key))(row));
                  return (
                    <td key={col.key} style={style}>
                      {content}
                    </td>
                  );
                })}
              </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

/** Render a raw cell value: primitives become text, nullish becomes empty. */
function asNode(value: unknown): ReactNode {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return null;
}
